import { Badge, Box } from "@salestrak/ui";
import type { ApiKeyPermissionSet } from "#app/lib/data";

type Permissions = Omit<ApiKeyPermissionSet, "__typename">;

const FULL_PERMISSIONS: Permissions = {
  all: true,
  create: true,
  read: true,
  update: true,
  delete: true,
};

const PERMISSION_LABELS: { key: keyof Omit<Permissions, "all">; label: string; description: string }[] = [
  { key: "read", label: "Read", description: "View workspace data" },
  { key: "create", label: "Create", description: "Create new entities" },
  { key: "update", label: "Update", description: "Modify existing entities" },
  { key: "delete", label: "Delete", description: "Remove entities" },
];

export function getPermissionsLabel(permissions: Permissions): string {
  if (permissions.all) return "Full access";

  const active = PERMISSION_LABELS
    .filter((p) => permissions[p.key])
    .map((p) => p.label);

  return active.length > 0 ? active.join(", ") : "No permissions";
}

interface PermissionViewProps {
  permissions: Permissions;
}

export function PermissionBadges({ permissions }: PermissionViewProps) {
  // "Full access" stands in for the whole per-permission list, so it takes the
  // filled badge for emphasis while individual permissions read as outlines.
  if (permissions.all) {
    return <Badge color="primary" tone="solid">Full access</Badge>;
  }

  return (
    <Box direction="row" gap="xs" wrap>
      {PERMISSION_LABELS.map((p) =>
        permissions[p.key] ? (
          <Badge key={p.key} tone="outline">
            {p.label}
          </Badge>
        ) : null,
      )}
    </Box>
  );
}

export { FULL_PERMISSIONS, PERMISSION_LABELS };
export type { Permissions };
